// QRCodeScanner.js
import { Html5Qrcode } from "html5-qrcode";
import { useState, useEffect, useRef, useCallback } from "react";

import styles from "./QRCodeScanner.module.css";

import qrIcon from "../assets/images/qr.png";
import collapseIcon from "../assets/images/selectArrowGrey.png";

import bagIcon from "../assets/images/iconShoppingBag.png";
import helpers from "../helpers.js";
import TextInput from "./TextInput.js";
import Button from "./Button.js";

const CAMERA_STORAGE_KEY = "bbb_qr_camera_id";

const normalizeCharcode = (raw) => {
  const s = String(raw ?? "").trim();
  const m = s.match(/CHA-(\d{1,6})/i);
  if (!m) return null;
  const digits = m[1].padStart(6, "0");
  return `CHA-${digits}`;
};

const asCharcodeString = (x) => (typeof x === "string" ? x : x?.charcode);

const QRScanner = (props) => {
  const [scannerReady, setScannerReady] = useState(false);
  const [collapseBags, setCollapseBags] = useState(false);
  const [displayError, setDisplayError] = useState([false, ""]);
  const [charcodeManualValue, setCharcodeManualValue] = useState("CHA-00");

  const [cameras, setCameras] = useState([]); // [{id,label}]
  const [cameraId, setCameraId] = useState(""); // selected id
  const [showCameraMenu, setShowCameraMenu] = useState(false);

  const audioRef = useRef(null);
  const html5Ref = useRef(null);          // Html5Qrcode instance
  const runningRef = useRef(false);       // whether camera is running
  const switchingRef = useRef(false);     // prevent overlap
  const inFlightRef = useRef(false);

  // Cooldowns / timers
  const cooldownRef = useRef(false);
  const errorTimerRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio("/scanned.mp3");
    audioRef.current.preload = "auto";
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, []);

  const playSound = () => {
    audioRef.current?.play?.().catch((error) => {
      console.error("Error playing audio", error);
    });
  };

  const checkCharcodeExists = useCallback(
    async (charcode) => {
      try {
        const res = await fetch(`${props.backEndURL}/charcode/${charcode}/`);
        const json = await res.json();
        return Boolean(json?.success);
      } catch (e) {
        console.error("Charcode validation failed:", e);
        return false;
      }
    },
    [props.backEndURL]
  );

  // ✅ Always show errors for at least 1 second
  const showTempError = useCallback((msg, ms = 1000) => {
    setDisplayError([true, msg]);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      setDisplayError([false, ""]);
      errorTimerRef.current = null;
    }, ms);
  }, []);

  // Helper: block scanning for N ms (prevents spam on same QR)
  const setCooldown = (ms) => {
    cooldownRef.current = true;
    setTimeout(() => (cooldownRef.current = false), ms);
  };

  const onScanSuccess = useCallback(
    async (rawResult) => {
      // ✅ global cooldown (default 700ms)
      if (cooldownRef.current) return;
      setCooldown(700);

      if (props.totalBagsExpected && props.scanResults.length + 1 > props.totalBagsExpected) {
        showTempError("Exceeding Order Amount", 1000);
        return;
      }

      const normalized = normalizeCharcode(rawResult);
      if (!normalized) {
        showTempError("Invalid Charcode Format", 1000);
        return;
      }

      const already = props.scanResults.map(asCharcodeString).some((c) => c === normalized);
      if (already) {
        // ✅ Longer cooldown so it doesn't spam when QR stays in view
        setCooldown(1000);
        showTempError("Charcode added", 1000);
        return;
      }

      if (!props.backEndURL) {
        showTempError("Scanner misconfigured (no backend URL)", 1000);
        return;
      }

      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const exists = await checkCharcodeExists(normalized);

      inFlightRef.current = false;

      if (!exists) {
        showTempError("Charcode not registered, cannot be added to order", 1000);
        return;
      }

      playSound();
      props.addCharcode(normalized);
      setCharcodeManualValue("CHA-00");
    },
    [
      props.scanResults,
      props.totalBagsExpected,
      props.addCharcode,
      props.backEndURL,
      checkCharcodeExists,
      showTempError
    ]
  );

  // create Html5Qrcode instance once
  useEffect(() => {
    html5Ref.current = new Html5Qrcode("reader");
    return () => {
      (async () => {
        try {
          if (runningRef.current) {
            await html5Ref.current?.stop?.();
            runningRef.current = false;
          }
        } catch {}
        try {
          await html5Ref.current?.clear?.();
        } catch {}
        html5Ref.current = null;
      })();
    };
  }, []);

  // get camera list once
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (cancelled) return;

        const list = (devices || []).map((d, i) => ({
          id: d.id,
          label: d.label || `Camera ${i + 1}`,
        }));

        setCameras(list);
        if (!list.length) return;

        let storedId = "";
        try {
          storedId = localStorage.getItem(CAMERA_STORAGE_KEY) || "";
        } catch {}

        const storedIsValid = storedId && list.some((c) => c.id === storedId);
        if (storedIsValid) {
          setCameraId(storedId);
          return;
        }

        const back = list.find((c) => /back|rear|environment/i.test(c.label));
        const fallback = back?.id || (list[1]?.id ?? list[0].id);
        setCameraId(fallback);

        try {
          localStorage.setItem(CAMERA_STORAGE_KEY, fallback);
        } catch {}
      } catch (e) {
        console.error("Failed to get cameras", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Always keep a ref to the latest onScanSuccess so the stable wrapper below
  // never goes stale without needing to restart the camera.
  const onScanSuccessRef = useRef(onScanSuccess);
  useEffect(() => {
    onScanSuccessRef.current = onScanSuccess;
  });
  const stableOnScanSuccess = useCallback((rawResult) => {
    onScanSuccessRef.current(rawResult);
  }, []);

  // start/stop camera when cameraId changes
  useEffect(() => {
    if (!cameraId) return;
    if (!html5Ref.current) return;

    let cancelled = false;

    const start = async () => {
      if (switchingRef.current) return;
      switchingRef.current = true;

      try {
        setScannerReady(false);

        if (runningRef.current) {
          try {
            await html5Ref.current.stop();
          } catch {}
          runningRef.current = false;
        }
        if (cancelled) return;

        const config = { fps: 10 };

        await html5Ref.current.start(
          { deviceId: { exact: cameraId } },
          config,
          stableOnScanSuccess,
          () => {}
        );

        runningRef.current = true;
        if (!cancelled) setScannerReady(true);
      } catch (e) {
        console.error("Failed to start camera", e);
        showTempError("Camera failed to start. Check permissions + use HTTPS (or localhost).", 1000);
      } finally {
        switchingRef.current = false;
      }
    };

    start();

    return () => {
      cancelled = true;
    };
  }, [cameraId, stableOnScanSuccess, showTempError]);

  const handleSelectCamera = async (newId) => {
    setCameraId(newId);
    try {
      localStorage.setItem(CAMERA_STORAGE_KEY, newId);
    } catch {}
    setShowCameraMenu(false);

    // ✅ optional: small cooldown so switching doesn't instantly scan again
    setCooldown(700);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") onScanSuccess(charcodeManualValue);
  };

  const tooManyBags =
    props.totalBagsExpected && props.scanResults.length > props.totalBagsExpected;

  return (
    <div className={styles.container}>
      <div className={styles.header}>Scan Additional Charcodes</div>

      <div className={styles.qrContainerVertical}>
        {scannerReady ? (
          <>
            <img src={qrIcon} className={styles.qrIcon} />

            <div className={styles.manualEntryContainer}>
              <TextInput
                name={""}
                labelStyle={"top"}
                value={charcodeManualValue}
                onChange={setCharcodeManualValue}
                onKeyDown={handleKeyPress}
                highlighted={displayError[0]}
              />
              <img
                onClick={() => onScanSuccess(charcodeManualValue)}
                src={collapseIcon}
                className={styles.manualEntryIconWhite}
              />
            </div>
          </>
        ) : null}

        <div className={styles.reada} id="reader" />

        {cameras.length > 1 && (
          <div className={styles.cameraSwitcherBelow}>
            <button
              type="button"
              className={styles.switchCameraBtn}
              onClick={() => setShowCameraMenu((v) => !v)}
            >
              Switch camera
            </button>

            {showCameraMenu && (
              <div className={styles.cameraDropdown}>
                <select
                  className={styles.cameraSelect}
                  value={cameraId}
                  onChange={(e) => handleSelectCamera(e.target.value)}
                >
                  {cameras.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {displayError[0] ? (
          <div className={helpers.clx(styles.invalidCharcodeError)}>{displayError[1]}</div>
        ) : (
          ""
        )}
      </div>

      <div className={styles.bagsNumber} onClick={() => setCollapseBags(!collapseBags)}>
        <div className={styles.fontAwesome}>
          <img src={bagIcon} className={styles.bagIcon} />
        </div>
        Bags Scanned {" (" + props.scanResults.length + "/" + props.totalBagsExpected + ")"}
        <img
          src={collapseIcon}
          className={helpers.clx(styles.collapseIcon, collapseBags ? styles.collapsedIcon : "")}
        />
      </div>

      <div
        className={helpers.clx(
          styles.scannedCodes,
          props.scanResults.length > 15 ? styles.scannedCodesScrollable : "",
          collapseBags ? styles.scannedCodesCollapsed : ""
        )}
      >
        {props.scanResults.map((charcodeUrl) => {
          const charcode =
            typeof charcodeUrl === "string"
              ? charcodeUrl.substring(charcodeUrl.length - 10)
              : charcodeUrl.charcode.substring(charcodeUrl.charcode.length - 10);

          return (
            <div
              key={typeof charcodeUrl === "string" ? charcodeUrl : charcodeUrl.charcode}
              className={helpers.clx(styles.charcodeTag, collapseBags ? styles.charcodeTagCollapsed : "")}
              onClick={() => props.removeCharcode(charcodeUrl)}
            >
              {charcode}
              <div className={styles.backspaceIcon}>backspace</div>
            </div>
          );
        })}
      </div>

      <div className={styles.buttonContainer}>
        <Button
          color={"Coal"}
          disabled={tooManyBags}
          customStyle={tooManyBags ? { opacity: "0.6" } : {}}
          name={props.closeScannerLabel || "Back"}
          onPress={() => props.closeScanner()}
        />
      </div>
    </div>
  );
};

export default QRScanner;
